"use client";

import { AlertCircle, Check, Loader2, Palette, Plus, Search, X } from "lucide-react";

import type { ICatalogItem } from "@/@types/campaign";
import type { CampaignConflictItem, ConflictingCampaign } from "@/@types/campaign-conflict";
import type { IGradeTarget } from "@/@types/campaign-grade";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useAddItems } from "../hooks/use-add-items";
import type { AddItemsState } from "../types";
import {
  conflictGradesLabel,
  conflictPeriodLabel,
  conflictStoresLabel,
} from "../utils/conflict-scope";
import { ADD_ITEM_TEXTS } from "../utils/messages";
import {
  GRADE_BUTTON_ARIA,
  GRADE_BUTTON_LABEL,
  GRADE_BUTTON_TITLE,
  gradeCountLabel,
} from "./grade/grade-messages";
import { GradeTargetModal } from "./grade/grade-target-modal";
import { ItemsWithoutGradeAlert } from "./grade/items-without-grade-alert";
import { CAMPAIGN_TYPES } from "./edit-campaign/constants";

const SEARCH_INPUT_ID = "add-item-search";
const DEFAULT_LIST_COUNT = 1;
const SCOPE_SEPARATOR = " · ";

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignCode: number;
  campaignType: string;
  numLists?: number;
  gradeRestricted?: boolean;
  onSuccess: () => void;
}

interface SelectedChipProps {
  item: ICatalogItem;
  state: AddItemsState;
  showGrade: boolean;
}

const describeConflictLine = (conflict: CampaignConflictItem, campaign: ConflictingCampaign): string =>
  [
    ADD_ITEM_TEXTS.CONFLICT(conflict.itemCode, conflict.itemName, campaign.description, campaign.code),
    conflictGradesLabel(campaign),
    conflictStoresLabel(campaign),
    conflictPeriodLabel(campaign),
  ]
    .filter((part: string | null): part is string => Boolean(part))
    .join(SCOPE_SEPARATOR);

function SelectedChip({ item, state, showGrade }: SelectedChipProps): JSX.Element {
  const grades: IGradeTarget[] | undefined = state.gradesBySku[item.code];

  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs text-primary">
      {item.code}
      {showGrade && (
        <button
          type="button"
          title={GRADE_BUTTON_TITLE}
          aria-label={GRADE_BUTTON_ARIA(item.code)}
          onClick={() => state.openGrade(item)}
          className="inline-flex items-center gap-1 rounded-full px-1 hover:bg-primary/20"
        >
          <Palette className="h-3 w-3" aria-hidden />
          {(grades?.length ?? 0) > 0 ? gradeCountLabel(grades) : GRADE_BUTTON_LABEL}
        </button>
      )}
      <button
        type="button"
        aria-label={ADD_ITEM_TEXTS.REMOVE_SELECTED(item.code)}
        onClick={() => state.removeItem(item.code)}
        className="rounded-full p-0.5 hover:bg-primary/20"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

export function AddItemModal({
  isOpen,
  onClose,
  campaignCode,
  campaignType,
  numLists = DEFAULT_LIST_COUNT,
  gradeRestricted = false,
  onSuccess,
}: AddItemModalProps): JSX.Element {
  const state: AddItemsState = useAddItems({
    campaignCode,
    campaignType,
    gradeRestricted,
    onAdded: () => {
      onSuccess();
      onClose();
    },
  });
  const isPro017: boolean = campaignType.toUpperCase() === CAMPAIGN_TYPES.PRO017;
  const showGrade: boolean = gradeRestricted && !isPro017;

  const handleClose = (): void => {
    state.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" aria-hidden />
            {ADD_ITEM_TEXTS.TITLE}
          </DialogTitle>
          <DialogDescription>{ADD_ITEM_TEXTS.DESCRIPTION(campaignCode)}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-auto">
          {isPro017 && numLists > 1 && (
            <div className="space-y-2">
              <Label>{ADD_ITEM_TEXTS.LIST_LABEL}</Label>
              <Select value={state.selectedList} onValueChange={state.setSelectedList}>
                <SelectTrigger aria-label={ADD_ITEM_TEXTS.LIST_LABEL}>
                  <SelectValue placeholder={ADD_ITEM_TEXTS.LIST_PLACEHOLDER} />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: numLists }, (_value: unknown, index: number) => (
                    <SelectItem key={index + 1} value={String(index + 1)}>
                      {ADD_ITEM_TEXTS.LIST_OPTION(index + 1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor={SEARCH_INPUT_ID}>{ADD_ITEM_TEXTS.SEARCH_LABEL}</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                id={SEARCH_INPUT_ID}
                value={state.searchQuery}
                onChange={(event) => state.setSearchQuery(event.target.value)}
                placeholder={ADD_ITEM_TEXTS.SEARCH_PLACEHOLDER}
                className="pl-9"
              />
              {state.isSearching && (
                <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" aria-hidden />
              )}
            </div>
          </div>

          {state.debouncedQuery && state.searchResults && (
            <div className="max-h-[200px] overflow-y-auto rounded-md border">
              {state.searchResults.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {ADD_ITEM_TEXTS.NO_RESULTS}
                </p>
              )}
              {state.searchResults.map((item: ICatalogItem) => (
                <label
                  key={item.code}
                  className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted"
                >
                  <Checkbox
                    checked={state.isItemSelected(item.code)}
                    onCheckedChange={() => state.toggleItem(item)}
                  />
                  <span className="flex-shrink-0 font-mono text-sm">{item.code}</span>
                  <span className="truncate text-sm text-muted-foreground">{item.name}</span>
                </label>
              ))}
            </div>
          )}

          {state.selectedItems.length > 0 && (
            <div className="space-y-2">
              <Label>{ADD_ITEM_TEXTS.SELECTED(state.selectedItems.length)}</Label>
              <div className="flex flex-wrap gap-1.5">
                {state.selectedItems.map((item: ICatalogItem) => (
                  <SelectedChip key={item.code} item={item} state={state} showGrade={showGrade} />
                ))}
              </div>
            </div>
          )}

          <ItemsWithoutGradeAlert itemCodes={state.itemsWithoutGrade} />

          {state.conflicts.length > 0 && (
            <div className="space-y-2">
              {state.conflicts.map((conflict: CampaignConflictItem) => (
                <div
                  key={conflict.itemCode}
                  className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" aria-hidden />
                  <div className="space-y-1 text-sm">
                    {conflict.conflictingCampaigns.map((campaign: ConflictingCampaign, index: number) => (
                      <p key={`${campaign.code}-${index}`}>{describeConflictLine(conflict, campaign)}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            {ADD_ITEM_TEXTS.CANCEL}
          </Button>
          <Button onClick={() => void state.add()} disabled={state.isAdding || !state.canAdd}>
            {state.isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {!state.isAdding && <Check className="mr-2 h-4 w-4" aria-hidden />}
            {state.isAdding ? ADD_ITEM_TEXTS.ADDING : ADD_ITEM_TEXTS.ADD(state.selectedItems.length)}
          </Button>
        </div>

        {state.gradeItem && (
          <GradeTargetModal
            isOpen
            itemCode={state.gradeItem.code}
            itemName={state.gradeItem.name}
            initialTargets={state.gradesBySku[state.gradeItem.code] ?? []}
            onConfirm={state.confirmGrades}
            onClose={state.closeGrade}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
